import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import CommonImage from '@components/CommonImage';
import CustomDropdown from '@components/CustomDropdown';
import { toastError, toastSuccess } from '@components/Toast';

import { incoTerms } from '@features/order/utils/projectUtil';

import { fetchOrderDetailsInfo } from '@store/orders/actions';

import { fetchEnumValues, findAllUsersByUserTypes } from '@services/commonService';
import { fetchBrandWithSubBrand } from '@services/orderService';

import ApiClient from '@lib/apiClient';

import { changeDateFormat, getFileType, getIconByFileType, getShortName, onUploadFile } from '@utils/Util';

const BasicInfo = ({ orderId }) => {
	const [projectDetails, setProjectDetails] = useState(null);
	const [poDocumentResponse, setPoDocumentResponse] = useState([]);
	const [salesChannels, setSalesChannels] = useState([]);
	const [selectedSalesChannel, setSelectedSalesChannel] = useState('');
	const [selectedIncoTerm, setSelectedIncoTerm] = useState('');
	const [projectManagers, setProjectManagers] = useState([]);
	const [selectedProjectManager, setSelectedProjectManager] = useState('');
	const [selectedAccountManager, setSelectedAccountManager] = useState('');
	const [accountManagers, setAccountManagers] = useState([]);
	const [, setLoading] = useState(false);
	const [subBrands, setSubBrands] = useState([]);
	const [selectedSubBrand, setSelectedSubBrand] = useState('');

	const [errors, setErrors] = useState({
		orderTitleError: '',
		orderStartDateError: '',
		salesChannelError: '',
		projectManagerError: '',
		accountManagerError: ''
	});
	const orderInfoRef = useRef(null);
	const [isReadOnly] = useState(false);
	const dispatch = useDispatch();
	useEffect(() => {
		(async () => {
			try {
				if (projectDetails?.brandResponse?.id) {
					let params = `${projectDetails?.brandResponse?.id}/brands-with-sub-brands`;
					const data = await fetchBrandWithSubBrand(params);
					setSubBrands(
						data?.subBrands?.map((item) => ({ id: item.id, value: item.name, label: item.name }))
					);
				}
			} catch {}
		})();
	}, [projectDetails?.brandResponse?.id]);

	useEffect(() => {
		(async () => {
			try {
				const data = await findAllUsersByUserTypes(
					`?userTypes=PROJECT_MANAGER&userTypes=ACCOUNT_MANAGER`
				);
				setProjectManagers(
					data.PROJECT_MANAGER?.map((item) => ({ label: item.id, value: item.name }))
				);
				setAccountManagers(data.ACCOUNT_MANAGER?.map((item) => ({ id: item.id, value: item.name })));
			} catch {}
		})();
	}, []);

	const getProjectDetails = async (id) => {
		setLoading(true);
		await ApiClient.GET('getOrderDetails', +id)
			.then(async ({ data }) => {
				setLoading(false);
				setProjectDetails({
					...data,
					startDate: changeDateFormat(data?.startDate, 'DD/MM/YYYY', 'YYYY-MM-DD')
				});
				if (data.poDocumentResponse) {
					setPoDocumentResponse(data.poDocumentResponse);
				}
				const streamData = await fetchEnumValues('info.nitex.app.common.enums.OrderStream');
				setSalesChannels(streamData?.map((item) => ({ value: item.name, id: item.constant })));

				if (data?.stream) {
					let selectedStream = streamData.find((stream) => stream.constant === data.stream);
					if (selectedStream !== undefined) {
						setSelectedSalesChannel({
							id: selectedStream.constant,
							value: selectedStream.name
						});
					}
				}
				if (data?.projectManagerResponse?.id) {
					setSelectedProjectManager({
						label: data.projectManagerResponse.name,
						value: data.projectManagerResponse.id
					});
				}
				if (data?.accountManagerResponse?.id) {
					setSelectedAccountManager({
						label: data.accountManagerResponse.name,
						value: data.accountManagerResponse.id
					});
				}
				if (data?.subBrandResponse?.id) {
					setSelectedSubBrand({
						label: data.subBrandResponse.name,
						value: data.subBrandResponse.id
					});
				}
				if (data?.incoterms) {
					setSelectedIncoTerm({
						label: data.incoterms,
						value: data.incoterms,
						id: data.incoterms
					});
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	useEffect(() => {
		getProjectDetails(orderId);
	}, [orderId]);

	const onUploadPo = async (poData) => {
		setLoading(true);
		await ApiClient.POST('addPoToOrder', poData, orderId)
			.then(({ data }) => {
				setLoading(false);
				if (data && data.message) {
					setPoDocumentResponse((prev) => [
						...prev,
						{ name: data.name, id: data.id, docUrl: data.docUrl }
					]);
					getProjectDetails(orderId);
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onRemovePo = async (itemId) => {
		setLoading(true);
		await ApiClient.DELETE('addPoToOrder', {}, `${orderId}/${itemId}`)
			.then(({ data }) => {
				setLoading(false);
				if (data) {
					const updatedList = poDocumentResponse.filter((item) => item.id !== itemId);
					setPoDocumentResponse(updatedList);
					toastSuccess('Removed successfully');
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const resetValidation = (fieldName, value) => {
		let isDataExist;
		if (Array.isArray(value)) {
			isDataExist = fieldName !== undefined && value.length > 0;
		} else {
			isDataExist = fieldName !== undefined && value.toString().length > 0;
		}

		if (isDataExist) {
			switch (fieldName) {
				case 'title':
					removeErrorFlag('orderTitleError');
					break;
				case 'buyerId':
					removeErrorFlag('orderBuyerIdError');
					break;
				case 'poNumber':
					removeErrorFlag('orderPoError');
					break;
				case 'startDate':
					removeErrorFlag('orderStartDateError');
					break;
				case 'projectManager':
					removeErrorFlag('projectManagerError');
					break;
				case 'accountManager':
					removeErrorFlag('accountManagerError');
					break;
				case 'salesChannel':
					removeErrorFlag('salesChannelError');
					break;
				case 'dueDate':
					removeErrorFlag('orderDeliveryDateError');
					break;
				default:
					break;
			}
		}
	};

	const removeErrorFlag = (flagName) => {
		let errorObj = { ...errors };
		if (flagName in errorObj) {
			errorObj[flagName] = undefined;
		}
		setErrors(errorObj);
	};

	const onUpdateOrderBasicInfo = async () => {
		let isValid = true;
		if (!projectDetails.name) {
			setErrors({ orderTitleError: 'Select a order title' });
			orderInfoRef.current?.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -300);
			return;
		}
		if (!selectedProjectManager?.value) {
			setErrors({ projectManagerError: 'Select project manager' });
			orderInfoRef.current?.scrollIntoView({ behavior: 'smooth' });
			return;
		}
		if (!selectedAccountManager?.value) {
			setErrors({ accountManagerError: 'Select account manager' });
			orderInfoRef.current?.scrollIntoView({ behavior: 'smooth' });
			return;
		}
		if (!selectedSalesChannel?.value) {
			setErrors({ salesChannelError: 'Select revenue source' });
			orderInfoRef.current?.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -300);
			return;
		}
		if (!projectDetails.startDate) {
			setErrors({ orderStartDateError: 'Select order receive date' });
			orderInfoRef.current?.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -300);
			return;
		}
		if (!selectedIncoTerm?.id) {
			setErrors({ incoTermError: 'Incoterm is required' });
			orderInfoRef.current?.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -300);
			return;
		}
		if (!projectDetails.incotermsLocation) {
			setErrors({ incoTermLocationError: 'Location is required' });
			orderInfoRef.current?.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -300);
			return;
		}
		if (!isValid) return;
		setErrors({});
		let body = {
			name: projectDetails.name,
			startDate: projectDetails.startDate,
			projectManagerId: selectedProjectManager?.value,
			accountManagerId: selectedAccountManager?.value,
			season: projectDetails?.season,
			incotermsLocation: projectDetails?.incotermsLocation
		};
		if (selectedSalesChannel?.id) {
			body['stream'] = selectedSalesChannel.id;
		}
		if (selectedSubBrand?.id) {
			body['subBrandId'] = selectedSubBrand.id;
		}
		if (selectedIncoTerm?.id) {
			body['incoterms'] = selectedIncoTerm.id;
		}
		setLoading(true);
		await ApiClient.POST('updateProjectBasicInfo', body, orderId)
			.then(({ data }) => {
				setLoading(false);
				getProjectDetails(orderId);
				dispatch(fetchOrderDetailsInfo(orderId));
				if (data.success) {
					toastSuccess(data.message);
				} else {
					toastError(data.message);
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const renderFileTypeWiseIcons = (file) => {
		let fileType;
		if (file.docUrl) {
			fileType = getFileType(file.docUrl);
		} else {
			fileType = getFileType(file.name);
		}
		return (
			<CommonImage
				src={getIconByFileType(fileType)}
				alt={'file image'}
				className="add-to-favorite"
				width="24"
				isStatic={false}
			/>
		);
	};

	return (
		<div className="accordion" id="accordionExample">
			<div className="card">
				<p className="medium-14 order-basic-info-title">Order basic information*</p>
				<div className="collapse show scroll-x-label">
					<div className="card-body radius-6 border-0">
						<div className="create-order-form">
							<div className="row">
								<div className="col-md-6 col-lg-3 col-xl-3 px-1">
									<div className="form-group">
										<label for="collection">Order title*</label>
										<input
											type="text"
											autoComplete="off"
											id="oder-title"
											name="title"
											placeholder="Order title"
											readOnly={isReadOnly}
											value={projectDetails?.name || ''}
											onChange={(e) => {
												setProjectDetails({
													...projectDetails,
													name: e.target.value
												});
												resetValidation('title', e.target.value);
											}}
										/>
										{errors.orderTitleError && (
											<span className="error">{errors.orderTitleError}</span>
										)}
									</div>
								</div>
								<div className="col-md-6 col-lg-3 col-xl-3 px-1">
									<div className="form-group">
										<div className="select-component">
											<label for="collection">Buyer*</label>
											<input
												readOnly={isReadOnly}
												className={'bg-gray-light border-0'}
												type="text"
												placeholder="Enter buyer email"
												value={projectDetails?.buyerResponse.email || ''}
												name="orderBuyer"
												disabled
												autoComplete="off"
											/>
										</div>
									</div>
								</div>
								<div className="col-md-6 col-lg-2 col-xl-2 px-1">
									<div className="form-group">
										<div className="select-component">
											<label for="collection">Project Manager*</label>
											<CustomDropdown
												title="Select"
												items={projectManagers}
												onItemClick={(selected) => {
													setSelectedProjectManager({
														label: selected.value,
														value: selected.label
													});
													resetValidation('projectManager', selected.value);
												}}
												toolTipLength={35}
												selectedItem={selectedProjectManager.label}
											/>
											{errors.projectManagerError && (
												<span className="error">{errors.projectManagerError}</span>
											)}
										</div>
									</div>
								</div>
								<div className="col-md-6 col-lg-2 col-xl-2 px-1">
									<div className="form-group">
										<div className="select-component">
											<label for="collection">Account Manager*</label>
											<CustomDropdown
												title="Select"
												items={accountManagers}
												onItemClick={(selected) => {
													setSelectedAccountManager({
														label: selected.value,
														value: selected.id
													});
													resetValidation('accountManager', selected.value);
												}}
												selectedItem={selectedAccountManager.label}
												toolTipLength={35}
											/>
											{errors.accountManagerError && (
												<span className="error">{errors.accountManagerError}</span>
											)}
										</div>
									</div>
								</div>
								<div className="col-md-6 col-lg-2 col-xl-2 px-1">
									<div className="form-group">
										<label htmlFor="collection">Season</label>
										<input
											type="text"
											className="w-100"
											placeholder="season"
											value={projectDetails?.season || ''}
											onChange={(e) => {
												setProjectDetails({
													...projectDetails,
													season: e.target.value
												});
											}}
										/>
									</div>
								</div>

								<div className="col-md-6 col-lg-3 col-xl-3 px-1">
									<div className="form-group">
										<label htmlFor="po-receive">PO receive date (mm/dd/yyyy)</label>
										<input
											type="date"
											id="po-receive"
											name="startDate"
											readOnly={isReadOnly}
											value={projectDetails?.startDate || ''}
											onChange={(e) => {
												setProjectDetails({
													...projectDetails,
													startDate: e.target.value
												});
												resetValidation('startDate', e.target.value);
											}}
										/>
										{errors.orderStartDateError && (
											<span className="error">{errors.orderStartDateError}</span>
										)}
									</div>
								</div>
								<div className="col-md-6 col-lg-3 col-xl-3 px-1">
									<div className="form-group ">
										<div className="select-component">
											<label htmlFor="collection">Sub-brand</label>
											<CustomDropdown
												title="Select"
												items={subBrands}
												onItemClick={(selected) => {
													setSelectedSubBrand({
														id: selected.id,
														value: selected.value,
														label: selected.label
													});
												}}
												selectedItem={selectedSubBrand.label}
												toolTipLength={35}
											/>
										</div>
									</div>
								</div>
								<div className="col-md-6 col-lg-2 col-xl-2 px-1">
									<div className="form-group">
										<div className="select-component">
											<label htmlFor="collection">Incoterm*</label>
											<CustomDropdown
												title="Select"
												items={incoTerms}
												onItemClick={(selected) => {
													setSelectedIncoTerm({
														id: selected.id,
														value: selected.value
													});
												}}
												selectedItem={selectedIncoTerm.value}
												toolTipLength={35}
											/>
											{errors.incoTermError && (
												<span className="error">{errors.incoTermError}</span>
											)}
										</div>
									</div>
								</div>
								<div className="col-md-6 col-lg-2 col-xl-2 px-1">
									<div className="form-group">
										<label htmlFor="collection">Incoterm location*</label>
										<input
											type="text"
											className="w-100"
											placeholder="inco term location"
											value={projectDetails?.incotermsLocation || ''}
											onChange={(e) => {
												setProjectDetails({
													...projectDetails,
													incotermsLocation: e.target.value
												});
											}}
										/>
										{errors.incoTermLocationError && (
											<span className="error">{errors.incoTermLocationError}</span>
										)}
									</div>
								</div>
								<div className="col-md-6 col-lg-2 col-xl-2 px-1">
									<div className="form-group revenue-form-group">
										<div className="select-component">
											<label for="collection">Revenue stream</label>

											<CustomDropdown
												title="Select"
												items={salesChannels}
												onItemClick={(selected) => {
													setSelectedSalesChannel({
														id: selected.id,
														value: selected.value
													});
												}}
												selectedItem={selectedSalesChannel.value}
												toolTipLength={35}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="create-order-form po-upload-and-file">
							<div className="row">
								<div className="col-md-12 pl-0">
									<div className="upload-img mt-2 d-flex align-items-center justify-content-start">
										<div className="file-uploader height-small upload-design-image m-0 mb-sm-0">
											<div className="d-flex justify-content-start align-items-center">
												<label
													for="drag-upload"
													className="drag-upload m-0 d-flex justify-content-center align-items-center"
												>
													<CommonImage src="/icons/upload-blue.svg" width="24" />
													<p className="ml-1">Upload PO</p>
												</label>

												{(!poDocumentResponse || poDocumentResponse.length === 0) && (
													<p className="regular-14 gray_dark_02 pl-4">
														No PO uploaded
													</p>
												)}
											</div>

											{!isReadOnly && (
												<input
													type="file"
													id="drag-upload"
													className="file-upload"
													name="image"
													multiple={true}
													onChange={(e) =>
														onUploadFile(
															e,
															'PURCHASE_ORDER',
															onUploadPo,
															'MULTIPLE'
														)
													}
													accept="*"
												/>
											)}
										</div>
										<div className="all-pdf-files ml-3 d-flex flex-wrap justify-content-start align-items-center">
											{poDocumentResponse?.map((item) => (
												<div
													key={item.id}
													className="single-pdf-file d-flex align-items-center mr-4"
												>
													{renderFileTypeWiseIcons(item)}
													<Tooltip title={item.name} placement="top" arrow>
														<a
															href={item.docUrl}
															target="_blank"
															rel="noopener noreferrer"
															className="regular-14 ml-1"
														>
															{getShortName(item.name, 15)}
														</a>
													</Tooltip>
													<div className="acton-img cursor-pointer ml-1">
														<CommonImage
															src={'/icons/delete_red.svg'}
															alt={'delete'}
															width="20"
															onClick={() => onRemovePo(item.id)}
														/>
													</div>
												</div>
											))}
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="update-button pt-1 text-right">
							<button
								disabled={isReadOnly}
								className="button size32"
								onClick={onUpdateOrderBasicInfo}
							>
								Update
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default BasicInfo;
